import React, { useState, useEffect } from 'react';
import * as styles from './QrCode.module.scss';
import { useSmallUpBp } from '../../../hooks/useMedia';
import Accordion from '../../accordion/Accordion';
import List from '../../list/List';
import step1 from '../../../../content/media-library/onboarding/1.svg';
import step2 from '../../../../content/media-library/onboarding/2.svg';
import step3 from '../../../../content/media-library/onboarding/3.svg';
import QrCodeDemo from '../../onboardingForm/images/qr-codes/qr-code-demo.svg';
import QrCodeProd from '../../onboardingForm/images/qr-codes/qr-code-prod.svg';
import phoneIcon from '../../../../content/media-library/onboarding/icon-phone.svg';
import idIcon from '../../../../content/media-library/onboarding/icon-id.svg';
import docsIcon from '../../../../content/media-library/onboarding/icon-docs.svg';
import timeIcon from '../../../../content/media-library/onboarding/icon-time.svg';
import infoIcon from '../../../../content/media-library/onboarding/icon-info.svg';

const QR_CODES = {
  QrCodeDemo,
  QrCodeProd
};

const TextTooltip = ({ text, heading, description }) => {
  return (
    <>
      <span className={styles.textTooltip} role="presentation">
        {text}
        <div>
          <p>{heading}</p>
          <p>{description}</p>
        </div>
      </span>{' '}
      <img src={infoIcon} alt="" width="16" />
    </>
  );
};

// This is a temporary solution until web onboarding flow supports referrals.
const QrCode = ({ type }) => {
  const [qrCode, setQrCode] = useState(null);
  const [applyUrl, setApplyUrl] = useState('https://www.starlingbank.com/app/signup/');
  const isSmallUp = useSmallUpBp();
  const isAccountancy = type === 'accountancy';

  useEffect(() => {
    setQrCode(QR_CODES[window.starlingbank?.getConfig('onboardingQrCode') || 'QrCodeProd']);

    if (window.starlingbank?.getConfig('appIntentProtocol') === 'starlingbankdemo://') {
      setApplyUrl('https://www.starlingbank.com/demoapp/signup/');
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.nextSteps}>
        <div>
          <h3>Next steps</h3>
          <div className={styles.steps}>
            <div className={styles.step}>
              <span className={styles.bullet}>
                <img src={step1} alt="Step one" />
              </span>
              {isSmallUp ? (
                <div>
                  <TextTooltip
                    text="Scan the QR code"
                    heading="How to scan a QR code"
                    description="Open your camera and point it at the QR code. Then, just tap on the notification that pops up, and you'll be taken to your app store."
                  />{' '}
                  with your phone and you’ll be taken to your app store.
                </div>
              ) : (
                <div>
                  Tap the ‘Get the app’ button which will take you to your app store.
                  <br />
                  <a className={[styles.ctaButton, styles.teal].join(' ')} href={applyUrl}>
                    Get the app
                  </a>
                </div>
              )}
            </div>
            <div className={styles.step}>
              <span className={styles.bullet}>
                <img src={step2} alt="Step two" />
              </span>
              <div>
                Download the Starling Bank app and apply for{' '}
                {isAccountancy
                  ? 'a business account'
                  : 'an account (have your photo ID at the ready and make sure you use the same number you used on the referral form)'}
                .
              </div>
            </div>
            <div className={styles.step}>
              <span className={styles.bullet}>
                <img src={step3} alt="Step three" />
              </span>
              <div>
                {isAccountancy
                  ? 'Our team will review your application and let you know once it’s approved.'
                  : 'Once your account is approved, we’ll send you a National Trust day pass.'}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.qrCode}>
          <img src={qrCode} alt="Download the app QR code" />
        </div>
      </div>
      {isAccountancy ? (
        <>
          <div className={styles.whatYouNeed}>
            <h4>What you'll need</h4>
            <div className={styles.gridContainer}>
              <div>
                <img src={phoneIcon} alt="Smartphone icon" />
                <span>A smartphone (and internet connection)</span>
              </div>
              <div>
                <img src={idIcon} alt="Valid ID icon" />
                <span>Valid photo ID, such as a passport or UK driving licence</span>
              </div>
              <div>
                <img src={docsIcon} alt="Documents icon" />
                <span>
                  If you have{' '}
                  <a
                    href="https://help.starlingbank.com/business/topics/setting-up-an-account/what-documents-do-i-need-to-open-a-starling-business-account/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    documents
                  </a>{' '}
                  which confirm the nature of your business at hand this will be easier
                </span>
              </div>
              <div>
                <img src={timeIcon} alt="Clock icon" />
                <span>
                  About ten minutes of your time. Then it’s over to our team to approve your details and open your
                  account.
                </span>
              </div>
            </div>
          </div>
          <div className={styles.accordionWrapper}>
            <Accordion id="eligibility-requirements" heading="Eligibility requirements" open={true}>
              <List
                icon="Tick"
                customPadding="no-padding"
                items={[
                  <List.Item text="The business owner must be a sole trader in respect of the business, meaning they are not in partnership with any other person in relation to the business" />,
                  <List.Item text="The business must <strong>not</strong> be owned, managed or run through a separately incorporated legal entity" />
                ]}
              />
            </Accordion>

            <div className={styles.notice}>
              <p>
                Please note, to open an account as a sole trader, customers must meet certain eligibility checks, which
                include looking at credit data.
              </p>
              <p>
                When applying for the account we will need evidence of trading activity, and the sole trader can’t
                engage in any of the prohibited activities set out in the{' '}
                <a href="/docs/legal/account/Account-Schedule-STCA.pdf" target="_blank">
                  sole trader current account terms
                </a>
                .
              </p>
            </div>

            <strong>
              Due to high demand there is currently a delay in business account opening times. We are working through
              applications as quickly as possible, please bear with us and thank you for your interest. Our customer
              service agents will not be able to speed up your application process if you get in contact.
            </strong>
          </div>
        </>
      ) : (
        <></>
      )}
    </div>
  );
};

export default QrCode;
